'use client';

import { SlidersHorizontal } from 'lucide-react';
import type { SearchActivitiesFilters } from '@funkey/ai-tools/schemas';
import { useI18n } from '~/features/i18n';
import { useActivityFilterDrawerStore } from '../activity-filter-drawer-store';
import { buildResultFilterChips, DRAWER_SECTION_BY_CHIP_ID } from './ai-filter-chips';
import { useActivityAiFilterChips } from './useActivityAiFilterChips';
import { useActivitySearchCtx } from './context';

/**
 * Read-only filter chips for an assistant search result, plus an "Edit filters" button (active
 * result only). Clicking a chip opens the drawer at the matching section.
 *
 * - **Active result** (`live`): chips come from the LIVE URL filter state, so EVERY applied
 *   filter shows — including drawer-only ones the AI tool can't express (locationType, Vaste
 *   locatie, categories, season, …).
 * - **Historical result**: chips come from that turn's tool snapshot.
 */
export const ActivityAiResultFilterChips: React.FC<{
  filters: SearchActivitiesFilters;
  live?: boolean;
  /** The full filter set snapshotted with this turn (historical results). Persisted on the
   * triggering user message, so old results faithfully show what was applied — including
   * drawer filters the AI tool never saw. Falls back to the tool snapshot when absent. */
  storedChips?: readonly {
    id: string;
    label: string;
    section?: string;
  }[];
}> = ({
  filters,
  live = false,
  storedChips
}) => {
  const i18n = useI18n();
  const openDrawer = useActivityFilterDrawerStore(s => s.open);
  const urlFilters = useActivitySearchCtx().useFilters();
  const liveChips = useActivityAiFilterChips();
  const snapshotChips = buildResultFilterChips(i18n, filters);
  const chipButton = (chip: {
    id: string;
    label: string;
  }, section: string | undefined) => <button key={chip.id} type="button" data-testid="ai-result-filter-chip" onClick={() => openDrawer('ai', section)} className="rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1 text-xs leading-none font-medium text-secondary-700 transition-colors hover:border-secondary hover:bg-secondary-100">
            {chip.label}
        </button>;
  if (!live) {
    // Prefer the persisted snapshot (the full filter set the user had applied that turn,
    // incl. drawer-only filters), and append any AI-applied traits/duration from the tool
    // output that the snapshot doesn't carry. Fall back to the tool snapshot for older
    // turns saved before snapshots existed.
    const historical = storedChips && storedChips.length > 0 ? [...storedChips, ...snapshotChips.filter(c => (c.id === 'traits' || c.id === 'duration' || c.id === 'categories') && !storedChips.some(s_0 => s_0.id === c.id))] : snapshotChips;
    if (historical.length === 0) return null;
    return <div className="flex flex-wrap items-center gap-1.5">
                {historical.map(chip_0 => chipButton(chip_0, chip_0.section))}
            </div>;
  }

  // Stale when the current (live) filters no longer match what this result was searched
  // with — e.g. the user changed filters in the drawer after the search ran, so the
  // message text/count is out of date until they re-run.
  const liveFilters = urlFilters.value;
  const arr = (a: readonly unknown[] | undefined | null) => a?.length ? [...a].sort() : [];
  const arrEq = (a_0: readonly unknown[] | undefined | null, b: readonly unknown[] | undefined | null) => JSON.stringify(arr(a_0)) === JSON.stringify(arr(b));
  // Only compare the filters this result actually reflects (the ones echoed in its
  // snapshot + written to the URL). Remaining drawer-only filters (regions, location,
  // duration, languages) persist across turns and are applied client-side via the ES
  // path, so the grid already honors them — they are NOT "changed since this result" and
  // must not trip the stale note (that made it fire on every fresh chat). Categories AND
  // traits ARE written to the URL now (resolved ids / importance levels), so a drawer edit
  // to either DOES stale the result.
  const isStale = (filters.participants ?? null) !== (liveFilters.participants ?? null) || (filters.minBudget ?? null) !== (liveFilters.minBudget ?? null) || (filters.maxBudget ?? null) !== (liveFilters.maxBudget ?? null) || !arrEq(filters.locationType, liveFilters.locationType) || !arrEq(filters.season, liveFilters.season) || !arrEq(filters.cleanliness, liveFilters.cleanliness) || !arrEq(filters.categoryIds, liveFilters.categories) || !arrEq(filters.subCategoryIds, liveFilters.subCategories) || !arrEq(filters.traits, (['teamwork', 'action', 'competition', 'learning'] as const).filter(trait => (liveFilters[trait]?.length ?? 0) > 0));
  return <div className="flex flex-col gap-1.5">
            {isStale ? <p data-testid="ai-result-filters-stale" className="text-[11px] leading-snug font-medium text-amber-700">
                    {i18n._('search.ai.results.filtersChanged', undefined, {
        message: 'Filters changed since this result — update the search to refresh it.'
      })}
                </p> : null}
            <div className="flex flex-wrap items-center gap-1.5">
                {liveChips.map(chip_1 => chipButton(chip_1, DRAWER_SECTION_BY_CHIP_ID[chip_1.id]))}
                {/* AI-applied filters that the live (URL) chips don't already show.
                    `duration`/`categories`/`traits` ARE all URL-backed now, so render here
                    only as a fallback when the live chip isn't present (e.g. a historical-style
                    edge); clicking opens + pre-fills the drawer at that section. */}
                {snapshotChips.filter(chip_2 => (chip_2.id === 'duration' || chip_2.id === 'categories' || chip_2.id === 'traits') && !liveChips.some(l => l.id === chip_2.id)).map(chip_3 => chipButton(chip_3, chip_3.section))}
                <button type="button" data-testid="ai-result-edit-filters-button" onClick={() => openDrawer('ai')} className="inline-flex items-center gap-1 rounded-full border border-blue-900/10 bg-white px-3 py-1 text-xs leading-none font-medium text-blue-900 transition-colors hover:border-secondary/40 hover:bg-secondary/5">
                    <SlidersHorizontal className="size-3" aria-hidden strokeWidth={2.25} />
                    {i18n._('search.ai.chip.editFilters', undefined, {
          message: 'Edit filters'
        })}
                </button>
            </div>
        </div>;
};